import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { getInvite } from "@/lib/api";
import { useModals } from "@/lib/modal";
import { JoinCta } from "@/components/Buttons";
import { Countdown } from "@/components/Countdown";
import { Loader2 } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Invite() {
  const { code } = useParams();
  const { setReferral } = useModals();
  const [inviter, setInviter] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (code) setReferral(code.toUpperCase());
    getInvite(code)
      .then(setInviter)
      .catch(() => setMissing(true));
  }, [code, setReferral]);

  return (
    <div className="relative min-h-screen bg-ink-950 overflow-hidden" data-testid="invite-page">
      <Helmet>
        <title>You're invited - Synaphy</title>
        <meta name="description" content="A Founding member invited you to Synaphy - the Physical World Intelligence Network. Claim your Founding position free." />
      </Helmet>
      <div className="absolute left-1/2 top-[-10%] -translate-x-1/2 h-[440px] w-[760px] rounded-full bg-signal-violet/[0.07] blur-[150px] pointer-events-none" />

      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between border-b border-white/5">
        <Link to="/" className="flex items-center gap-3 group" data-testid="invite-logo" aria-label="Synaphy home">
          <img src="/logo-lockup.png" alt="Synaphy - Intelligence for the physical world" className="h-14 w-auto object-contain drop-shadow-[0_0_18px_rgba(90,150,255,0.35)] transition-transform duration-300 group-hover:scale-105" />
        </Link>
        <span className="text-sm text-steel">Founding invite</span>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-20 sm:pt-28 pb-24">
        {!inviter && !missing ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-signal-cyan" />
          </div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="flex items-center gap-2.5"
              data-testid="invite-banner"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-signal-cyan opacity-60 soft-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal-cyan" />
              </span>
              <span className="text-sm text-steel">
                {missing ? "You were invited to Synaphy" : <><span className="text-mist" data-testid="inviter-name">{inviter.name}</span> invited you</>}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.12, ease: EASE }}
              className="mt-7 font-display font-bold tracking-tight leading-[1.04] text-5xl sm:text-7xl text-mist"
            >
              The physical world
              <br />
              is becoming <span className="text-gradient-data">data.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-steel"
            >
              {missing
                ? "A Founding member invited you to Synaphy, the Physical World Intelligence Network. Join the contributor waitlist free and secure your position before the Pilot phase."
                : `${inviter.name} is building the Physical World Intelligence Network and invited you in. Join free and secure your Founding position before the Pilot phase.`}
            </motion.p>

            {!missing && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.42 }}
                className="mt-5 text-sm text-steel"
                data-testid="inviter-stats"
              >
                {inviter.verified_invites} verified invites sent by {inviter.name}
              </motion.p>
            )}

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
              className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <JoinCta testid="invite-join-btn">Claim your Founding position</JoinCta>
              <Link
                to="/"
                data-testid="invite-explore-link"
                className="text-sm font-medium text-steel transition-colors hover:text-mist"
              >
                Explore Synaphy first
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
              className="mt-14"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-steel">
                Founding phase ends in
              </span>
              <div className="mt-4">
                <Countdown testid="invite-countdown" />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mt-12 text-sm text-steel/80"
              data-testid="invite-milestones"
            >
              Milestones: Spark · 5 invites - Connector · 10 - Network Builder · 25.
              Free to join. No payment or special software required.
            </motion.p>
          </>
        )}
      </main>
    </div>
  );
}
